import { useEffect, useMemo, useState } from 'react';
import { workouts } from '../../../services/api';
import type { WorkoutDetail } from '../../../services/api';
import { normalizeWorkoutStreamTimeline } from '../../../utils/workoutStructure';
import {
  type StreamPoint,
  extractStreamRaw,
  parseStreamData,
  computeRangeStats,
  findStreamIndicesForRange,
} from '../../../utils/workoutStream';
import { alignStreams, sliceStreamByTime, type AlignedPoint } from '../../../utils/comparison/alignStreams';
import {
  apiRangeToMetrics,
  comparisonMetricsAreEmpty,
  extractSelectedRangeCalculation,
  rangeStatsToMetrics,
  type ComparisonSideMetrics,
} from '../../../utils/comparison/computeComparisonDeltas';
import type { ComparisonSession, SegmentRange } from '../types';

export interface ComparisonStreamState {
  streamA: StreamPoint[];
  streamB: StreamPoint[];
  /** Full workout streams (not segment-sliced) — for multi-lap overlay. */
  fullStreamA: StreamPoint[];
  fullStreamB: StreamPoint[];
  aligned: AlignedPoint[];
  metricsA: ComparisonSideMetrics;
  metricsB: ComparisonSideMetrics;
  detailA: WorkoutDetail | null;
  detailB: WorkoutDetail | null;
  loading: boolean;
  error: string | null;
  /** True when full workout streams are loaded (independent of segment slice). */
  streamsReady: boolean;
  /** True while server comparison metrics are loading after Apply. */
  metricsLoading: boolean;
}

function effectiveSegment(
  stream: StreamPoint[],
  segment: SegmentRange | null,
  viewMode: ComparisonSession['viewMode'],
): { startSec: number; endSec: number } | null {
  if (!stream.length) return null;
  if (viewMode === 'full') return null;
  if (segment) return segment;
  return { startSec: stream[0].timeSeconds, endSec: stream[stream.length - 1].timeSeconds };
}

/** Applied segments for server metrics — no implicit full-workout fallback. */
function segmentForMetrics(
  viewMode: ComparisonSession['viewMode'],
  segment: SegmentRange | null,
): SegmentRange | null {
  if (viewMode !== 'segments') return null;
  return segment;
}

function buildStreamMetrics(
  stream: StreamPoint[],
  seg: { startSec: number; endSec: number } | null,
  detail: WorkoutDetail | null,
): ComparisonSideMetrics {
  if (!stream.length) return {};
  const s = seg ?? { startSec: stream[0].timeSeconds, endSec: stream[stream.length - 1].timeSeconds };
  const { startIdx, endIdx } = findStreamIndicesForRange(stream, s.startSec, s.endSec);
  const stats = computeRangeStats(stream, startIdx, endIdx);
  const metrics = rangeStatsToMetrics(stats, detail?.distance ?? null);
  if (seg && metrics.durationSec == null) {
    metrics.durationSec = Math.max(0, seg.endSec - seg.startSec);
  }
  return metrics;
}

function fullWorkoutMetrics(detail: WorkoutDetail | null): ComparisonSideMetrics {
  return {
    durationSec: detail?.total_time ?? null,
    distanceM: detail?.distance ?? null,
    avgPower: detail?.np ?? null,
    np: detail?.np ?? null,
    tss: detail?.tss_actual ?? null,
    vi: detail?.vi != null ? Number(detail.vi) : null,
  };
}

function sliceForView(stream: StreamPoint[], seg: { startSec: number; endSec: number } | null): StreamPoint[] {
  if (!seg) return stream;
  return sliceStreamByTime(stream, seg.startSec, seg.endSec);
}

function workoutToSummary(w: WorkoutDetail, userId: number): import('../types').WorkoutSummary {
  return {
    id: w.id,
    userId,
    title: w.title ?? 'Workout',
    workout_day: w.workout_day ?? '',
    total_time: w.total_time ?? null,
    distance: w.distance ?? null,
    tss_actual: w.tss_actual ?? null,
    np: w.np ?? null,
    workout_type: w.workout_type,
  };
}

/** Unwrap GET /workout/{id}/details — zones, peaks, lap_data live here. */
function parseExtendedWorkoutDetails(apiBody: unknown): Record<string, unknown> | null {
  if (!apiBody || typeof apiBody !== 'object') return null;
  const root = apiBody as Record<string, unknown>;
  const envelope = root.data;
  if (envelope && typeof envelope === 'object') {
    const env = envelope as Record<string, unknown>;
    const nested = env.workoutDetails ?? env.data ?? env;
    if (nested && typeof nested === 'object') return nested as Record<string, unknown>;
  }
  const direct = root.workoutDetails ?? root;
  return direct && typeof direct === 'object' ? (direct as Record<string, unknown>) : null;
}

/** Merge lap_data + aggregates from /details onto the base workout record. */
function mergeWorkoutWithExtendedDetails(
  workout: WorkoutDetail | null | undefined,
  extended: Record<string, unknown> | null,
): WorkoutDetail | null {
  if (!workout) return null;
  if (!extended) return workout;

  const lapData = extended.lap_data;
  const laps = Array.isArray(lapData) ? lapData : workout.details?.lap_data ?? [];

  const baseDetails = workout.details ?? {
    zones: { hr_zones: [], pw_zones: [] },
    peaks: {},
    lap_data: [],
    min_max_avg: {},
  };

  return {
    ...workout,
    details: {
      ...baseDetails,
      zones: (extended.zones as typeof baseDetails.zones) ?? baseDetails.zones,
      peaks: (extended.peaks as typeof baseDetails.peaks) ?? baseDetails.peaks,
      lap_data: laps,
      min_max_avg: (extended.min_max_avg as typeof baseDetails.min_max_avg) ?? baseDetails.min_max_avg,
      data: baseDetails.data,
    },
  };
}

export interface ComparisonPreviewSegments {
  segmentA: SegmentRange | null;
  segmentB: SegmentRange | null;
}

/** Segments used for chart slicing (draft + live drag preview). */
export type ComparisonDisplaySegments = ComparisonPreviewSegments;

export function useComparisonStreams(
  session: ComparisonSession,
  fallbackUserId: number | null,
  compareTick: number,
  displaySegments: ComparisonDisplaySegments = { segmentA: null, segmentB: null },
  /** True when draft/preview segments differ from last applied session segments. */
  hasPendingSegmentChanges = false,
): ComparisonStreamState {
  const [streamA, setStreamA] = useState<StreamPoint[]>([]);
  const [streamB, setStreamB] = useState<StreamPoint[]>([]);
  const [detailA, setDetailA] = useState<WorkoutDetail | null>(null);
  const [detailB, setDetailB] = useState<WorkoutDetail | null>(null);
  const [metricsA, setMetricsA] = useState<ComparisonSideMetrics>({});
  const [metricsB, setMetricsB] = useState<ComparisonSideMetrics>({});
  const [loading, setLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const workoutAId = session.workoutA?.id ?? null;
  const workoutBId =
    session.mode === 'one-workout-segments' ? workoutAId : session.workoutB?.id ?? null;
  const userIdA = session.workoutA?.userId ?? fallbackUserId;
  const userIdB =
    session.mode === 'one-workout-segments'
      ? userIdA
      : session.workoutB?.userId ?? fallbackUserId;

  useEffect(() => {
    if (!workoutAId) {
      setStreamA([]);
      setStreamB([]);
      setDetailA(null);
      setDetailB(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const loadWorkout = async (id: number) => {
      const [detailRes, dataRes, detailsRes] = await Promise.all([
        workouts.getWorkoutDetail(id),
        workouts.getWorkoutData(id),
        workouts.getWorkoutDetails(id).catch(() => null),
      ]);
      const rawWorkout = detailRes.data?.data?.workout ?? detailRes.data?.data;
      const workout = mergeWorkoutWithExtendedDetails(
        rawWorkout,
        parseExtendedWorkoutDetails(detailsRes?.data),
      );
      const raw = extractStreamRaw(dataRes.data);
      let pts = parseStreamData(raw);
      const duration = workout?.total_time ? Number(workout.total_time) : pts[pts.length - 1]?.timeSeconds ?? 0;
      if (duration > 0) pts = normalizeWorkoutStreamTimeline(pts, duration);
      return { workout, pts };
    };

    void (async () => {
      try {
        const a = await loadWorkout(workoutAId);
        if (cancelled) return;
        setDetailA(a.workout);
        setStreamA(a.pts);

        if (session.mode === 'one-workout-segments') {
          setDetailB(a.workout);
          setStreamB(a.pts);
        } else if (workoutBId) {
          const b = await loadWorkout(workoutBId);
          if (cancelled) return;
          setDetailB(b.workout);
          setStreamB(b.pts);
        } else {
          setStreamB([]);
          setDetailB(null);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError((e as Error)?.message ?? 'Failed to load workout streams');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [workoutAId, workoutBId, session.mode]);

  const chartSegA = effectiveSegment(
    streamA,
    displaySegments.segmentA ?? session.segmentA,
    session.viewMode,
  );
  const chartSegB = effectiveSegment(
    streamB,
    displaySegments.segmentB ?? session.segmentB,
    session.viewMode,
  );
  const committedSegA = segmentForMetrics(session.viewMode, session.segmentA);
  const committedSegB = segmentForMetrics(session.viewMode, session.segmentB);

  const slicedA = useMemo(() => sliceForView(streamA, chartSegA), [streamA, chartSegA]);
  const slicedB = useMemo(() => sliceForView(streamB, chartSegB), [streamB, chartSegB]);

  const aligned = useMemo(() => {
    if (!slicedA.length && !slicedB.length) return [];
    return alignStreams(slicedA, slicedB, session.metric, session.alignment);
  }, [slicedA, slicedB, session.metric, session.alignment]);

  // Client-side metrics for chart preview — never calls calculateSelectedRange.
  // After Apply, keep server metrics until the user edits segments again.
  useEffect(() => {
    if (!streamA.length && !streamB.length) return;
    if (compareTick > 0 && !hasPendingSegmentChanges) {
      return;
    }

    const previewSegA = effectiveSegment(
      streamA,
      displaySegments.segmentA ?? session.segmentA,
      session.viewMode,
    );
    const previewSegB = effectiveSegment(
      streamB,
      displaySegments.segmentB ?? session.segmentB,
      session.viewMode,
    );

    if (session.viewMode === 'full') {
      setMetricsA(fullWorkoutMetrics(detailA));
      setMetricsB(
        session.mode === 'one-workout-segments'
          ? fullWorkoutMetrics(detailA)
          : fullWorkoutMetrics(detailB),
      );
      return;
    }

    setMetricsA(buildStreamMetrics(streamA, previewSegA, detailA));
    setMetricsB(
      session.mode === 'one-workout-segments'
        ? buildStreamMetrics(streamB, previewSegB, detailA)
        : buildStreamMetrics(streamB, previewSegB, detailB),
    );
  }, [
    streamA,
    streamB,
    displaySegments.segmentA,
    displaySegments.segmentB,
    session.segmentA,
    session.segmentB,
    session.viewMode,
    session.mode,
    detailA,
    detailB,
    compareTick,
    hasPendingSegmentChanges,
  ]);

  // Backend range metrics — only after user clicks Apply (compareTick).
  useEffect(() => {
    if (compareTick === 0 || !userIdA || !workoutAId) return;

    let cancelled = false;
    setMetricsLoading(true);

    const tryApiRange = async (
      workoutId: number,
      rangeUserId: number,
      stream: StreamPoint[],
      seg: SegmentRange | null,
      detail: WorkoutDetail | null,
    ): Promise<ComparisonSideMetrics> => {
      if (session.viewMode === 'full' || !seg) {
        return fullWorkoutMetrics(detail);
      }
      const { startIdx, endIdx } = findStreamIndicesForRange(stream, seg.startSec, seg.endSec);
      const startTs = stream[startIdx]?.timestamp;
      const endTs = stream[endIdx]?.timestamp;
      if (startTs != null && endTs != null) {
        try {
          const res = await workouts.calculateSelectedRange(workoutId, rangeUserId, startTs, endTs);
          const calc = extractSelectedRangeCalculation(res.data);
          const parsed = apiRangeToMetrics(calc);
          if (parsed.durationSec == null) {
            parsed.durationSec = Math.max(0, seg.endSec - seg.startSec);
          }
          if (!comparisonMetricsAreEmpty(parsed)) return parsed;
        } catch {
          /* fallback client-side */
        }
      }
      return buildStreamMetrics(stream, seg, detail);
    };

    void (async () => {
      try {
        const mA = await tryApiRange(workoutAId, userIdA, streamA, committedSegA, detailA);
        const mB =
          session.mode === 'one-workout-segments'
            ? await tryApiRange(workoutAId, userIdA, streamB, committedSegB, detailA)
            : workoutBId && userIdB
              ? await tryApiRange(workoutBId, userIdB, streamB, committedSegB, detailB)
              : ({} as ComparisonSideMetrics);

        if (!cancelled) {
          setMetricsA(mA);
          setMetricsB(mB);
        }
      } finally {
        if (!cancelled) setMetricsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API runs only when user clicks Apply (compareTick)
  }, [compareTick]);

  return {
    streamA: slicedA,
    streamB: slicedB,
    fullStreamA: streamA,
    fullStreamB: streamB,
    aligned,
    metricsA,
    metricsB,
    detailA,
    detailB,
    loading,
    error,
    streamsReady: streamA.length > 0 || streamB.length > 0,
    metricsLoading,
  };
}

export { workoutToSummary };
